import React from "react";
import { useNavigate } from "react-router-dom";

import Avatar from "../Avatar/Avatar";
import FollowButton from "../FollowButton";
import StatusIndicator from "../StatusIndicator/StatusIndicator";
import { PublicUser } from "../../types/social";

import styles from "./UserCard.module.scss";

export interface UserCardProps {
  /**
   * The user this card represents. Compatible with the PublicUser shape
   * from `client/src/types/social.tsx`.
   */
  user: Pick<PublicUser, "userId" | "username" | "avatarUrl" | "profileImageUrl">;

  /**
   * Optional secondary line (e.g. user "status" returned from the
   * Discover search endpoint). When absent or empty after trimming,
   * the status row is omitted from the rendered output.
   */
  status?: string | null;

  /**
   * The currently authenticated viewer's userId, or undefined if no
   * viewer is signed in. When this matches `user.userId`, the
   * FollowButton is omitted (the viewer cannot follow themselves).
   */
  currentUserId?: string;

  /**
   * Initial follow state to seed the embedded FollowButton.
   * Defaults to `false` if omitted.
   */
  initialIsFollowing?: boolean;

  /**
   * Whether there's a pending follow request to this user.
   * Defaults to `false` if omitted.
   */
  initialHasPendingRequest?: boolean;

  /**
   * Whether the target user has a private account.
   */
  isTargetPrivate?: boolean;

  /**
   * Forwarded from the embedded FollowButton's onFollowChange callback.
   * Receives the new follow state (`true` = now following, `false` = now unfollowed).
   */
  onFollowChange?: (isFollowing: boolean) => void;

  /**
   * Optional class applied to the root element so consumers can extend
   * layout styles without modifying the component.
   */
  className?: string;
}

export default function UserCard({
  user,
  status,
  currentUserId,
  initialIsFollowing,
  initialHasPendingRequest,
  isTargetPrivate,
  onFollowChange,
  className,
}: UserCardProps) {
  const navigate = useNavigate();

  const goToProfile = () => {
    navigate(`/profile?user=${user.userId}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      goToProfile();
    } else if (e.key === " ") {
      e.preventDefault();
      goToProfile();
    }
  };

  const rootClassName = [styles.userCard, className]
    .filter(Boolean)
    .join(" ");

  const showStatus = typeof status === "string" && status.trim().length > 0;
  const showFollowButton = currentUserId !== user.userId;

  return (
    <div
      className={rootClassName}
      role="button"
      tabIndex={0}
      aria-label={`Open ${user.username}'s profile`}
      onClick={goToProfile}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.info}>
        <div className={styles.avatarWrapper}>
          <Avatar
            src={user.profileImageUrl || user.avatarUrl}
            username={user.username}
            size={44}
          />
          <StatusIndicator userId={user.userId} variant="dot" />
        </div>

        <div className={styles.textBlock}>
          <div className={styles.username}>{user.username}</div>
          {showStatus ? (
            <div className={styles.status}>{status}</div>
          ) : null}
        </div>
      </div>

      <div
        className={styles.followBtnSlot}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {showFollowButton ? (
          <FollowButton
            targetUserId={user.userId}
            initialIsFollowing={initialIsFollowing ?? false}
            isTargetPrivate={isTargetPrivate ?? false}
            initialHasPendingRequest={initialHasPendingRequest ?? false}
            onFollowChange={(b) => onFollowChange?.(b)}
          />
        ) : null}
      </div>
    </div>
  );
}
